import { type Home, type User, UserRole } from '@lmc/ts-common'
import { Navigation } from '@lmc/ui-kit'
import { ssoLogout } from 'common/services/sso'
import { useAbilityAccess, useHasHomesList } from 'components/AccessControl'
import firebase from 'firebase/compat/app'
import useFeatureFlag from 'hooks/useFeatureFlags'
import useHomeType from 'hooks/useHomeType'
import { getCleanRoute } from 'hooks/useRouteMatch'
import { useRouter } from 'hooks/useRouter'
import type { FC } from 'react'
import { isBrowser, isTablet } from 'react-device-detect'
import { useSelector } from 'react-redux'
import { isGroupUser } from 'utils'
import { unsubscribe } from '../../common/services/firebase/data'
import { history } from '../../store'
import { FrontLinkItem } from './components'
import { getSections, isRouteSpecificPath } from './utils'

const isWeb = isBrowser || isTablet

type Props = { currentSectionKey?: string }

export const PrimaryNavigation: FC<Props> = ({ currentSectionKey }) => {
  const router = useRouter()

  const { hasRota } = useHomeType()

  const preferredPeopleNominative: string = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative || ''
  )

  const user = useSelector(
    (state: { global: { user: User & { privacyOfficer?: boolean } } }) =>
      state.global.user
  )

  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const carePlanNominative = useSelector(
    (state: { global: { home: { preferredCarePlansNominative: string } } }) =>
      state.global.home.preferredCarePlansNominative
  )

  const flags = useFeatureFlag({
    flagNames: ['outcomes_and_goals_v2', 'rostering_paywall_screen'] as const
  })?.data

  const params = router?.params

  const downloadCentreEnabled = useAbilityAccess([
    { i: 'download', this: 'Report' }
  ])

  const flagGoalsV2Enabled = flags?.outcomes_and_goals_v2.enabled
  const flagRosteringPaywallScreenEnabled =
    flags?.rostering_paywall_screen.enabled

  const goalsEnabled = flagGoalsV2Enabled && home?.features?.goals?.enabled

  const handleSignOut = async () => {
    try {
      if (firebase.apps.length) {
        const token = await firebase.messaging().getToken()
        await unsubscribe(token)
      }
    } finally {
      ssoLogout()
    }
  }

  const canViewResidents = useAbilityAccess([{ i: 'view', this: 'Resident' }])
  const canViewLogs = useAbilityAccess([{ i: 'view', this: 'Log' }])
  const canViewTodos = useAbilityAccess([{ i: 'view', this: 'TaskManagement' }])
  // CM-3765: Rota is not part of the IT & Security remit. Other roles' rota
  // visibility is unchanged (still gated only by the rostering feature flags).
  const canViewRota = user?.role !== UserRole.it
  const canViewReports = useAbilityAccess([{ i: 'view', this: 'Report' }])
  // Use view UserInvitation as a proxy: only admins and CARE_MANAGEMENT have it
  const canViewOrganisation = useAbilityAccess([
    { i: 'view', this: 'UserInvitation' }
  ])
  const canManageHome = useAbilityAccess([{ i: 'update', this: 'Home' }])
  // CM-3764: the Devices tab was gated on canManageHome (update Home), which the
  // IT role lacks, so IT couldn't reach Devices despite holding MobileDevice.
  // Gate on the MobileDevice *update* permission — the same check the device
  // screens use, so the tab and screens stay consistent. (Gating on `view` would
  // show the tab to view-only roles like Audit View but land them on the
  // screens' no-permission state.)
  const canManageDevices = useAbilityAccess([
    { i: 'update', this: 'MobileDevice' }
  ])
  // CM-3799: Shift Passwords is part of the IT & Security remit. The tab was
  // gated on canManageHome only; also allow anyone who can view shifts (IT).
  const canViewShiftPasswords = useAbilityAccess([{ i: 'view', this: 'Shift' }])
  const hasHomesList = useHasHomesList()
  const isGroupRole = isGroupUser(user?.role)

  const abilityMap = {
    canViewResidents,
    canViewLogs,
    canViewTodos,
    canViewRota,
    canViewReports,
    canViewOrganisation,
    canManageHome,
    canManageDevices,
    canViewShiftPasswords,
    hasHomesList,
    isGroupRole
  }

  const sections = getSections(
    user,
    home,
    preferredPeopleNominative,
    carePlanNominative,
    handleSignOut,
    downloadCentreEnabled,
    hasRota,
    goalsEnabled,
    canViewOrganisation,
    flagRosteringPaywallScreenEnabled,
    abilityMap
  )
    .filter((item) => item.canAccess !== false)
    .filter((item) => (isWeb ? item.isWeb === isWeb : true))

  const arrayPaths = sections.map((item) => item.key)

  const allTabsInactive = !arrayPaths.includes(currentSectionKey as string)

  const { pathname } = history.location

  const cleanRoute = getCleanRoute(pathname, {
    id: pathname.split('/')[3]
  })

  const flagsRotaTeamMemberNavException =
    hasRota && !cleanRoute.startsWith('/organisation/team/[id]')

  const isSubNavPath =
    (!hasRota && isRouteSpecificPath('/organisation')) ||
    (flagsRotaTeamMemberNavException && isRouteSpecificPath('/organisation')) ||
    isRouteSpecificPath('/todos') ||
    isRouteSpecificPath('/rota')

  return (
    <Navigation
      tabs={sections}
      rightChild={
        <FrontLinkItem user={user} home={home} handleSignOut={handleSignOut} />
      }
      router={{ ...history, params }}
      allTabsInactive={allTabsInactive}
      activeKey={pathname.split('/')?.[1] || currentSectionKey}
      withSubNav={isSubNavPath}
    />
  )
}
